// Client wrappers for the Phase-1 Platform Admin callables (tenant 360, members,
// user search, subscribers, audit). All are platform-admin guarded server-side.
import { call } from './callable';

export type MemberRole = 'owner' | 'admin' | 'agent';

export interface QuotaOverrides {
  subscribers?: number;
  messagesPerMonth?: number;
  aiTokensPerMonth?: number;
  teamSeats?: number;
}

export interface TenantDetail {
  tenant: {
    id: string; name: string; status: string; planId: string; createdAt: number;
    notes: string; tags: string[]; suspendReason: string | null;
    credits: { bonusMessages: number; freeUntil: number | null };
    overrides: QuotaOverrides;
    quotas: { subscribers: number; messagesPerMonth: number; aiTokensPerMonth: number; teamSeats: number };
  };
  owner: { uid: string; email?: string | null; displayName?: string | null } | null;
  usage: { subscribers: number; messages: number; aiTokens: number; month: string };
  health: { connected: boolean; isLive: boolean; phoneNumberId: string | null; verifiedPhoneE164: string | null };
  onboarding: { signedUp: boolean; connected: boolean; templateApproved: boolean; firstBroadcast: boolean };
  memberCount: number;
}

export interface AdminMember {
  uid: string; role: MemberRole; email: string | null; displayName: string | null;
  disabled: boolean; lastSignIn: string | null;
}

export interface AdminSubscriber {
  id: string; phoneE164: string; name: string; labels: string[];
  optedIn: boolean;
  marketingOptIn: boolean;
  /** Set when the contact sent STOP — such a contact is tombstoned, never deleted. */
  optedOutAt: number | null;
  createdAt: number;
}

export interface AuditEntry {
  id: string; actorEmail?: string | null; action: string; tenantId?: string;
  targetId?: string; reason?: string; at: number;
}


const padminTenantsC = call<any, any>('padminTenants');
const padminAuditC = call<any, any>('padminAudit');

export const getTenant = async (tenantId: string): Promise<TenantDetail> =>
  (await padminTenantsC({ action: 'getTenant', tenantId })).data;
export const updateTenant = async (p: { tenantId: string; name?: string; notes?: string; tags?: string[] }) =>
  (await padminTenantsC({ action: 'updateTenant', ...p })).data;
export const setOverrides = async (tenantId: string, overrides: QuotaOverrides) =>
  (await padminTenantsC({ action: 'setOverrides', tenantId, overrides })).data;
export const grantCredits = async (p: { tenantId: string; bonusMessages?: number; freeDays?: number; reason: string }) =>
  (await padminTenantsC({ action: 'grantCredits', ...p })).data;
export const listMembers = async (tenantId: string) =>
  (await padminTenantsC({ action: 'listMembers', tenantId })).data.members;
export const setMemberRole = async (tenantId: string, uid: string, role: MemberRole) =>
  (await padminTenantsC({ action: 'setMemberRole', tenantId, uid, role })).data;
export const removeMember = async (tenantId: string, uid: string, reason?: string) =>
  (await padminTenantsC({ action: 'removeMember', tenantId, uid, reason })).data;
export interface UserLookupResult {
  user: {
    uid: string;
    email: string | null;
    displayName: string | null;
    disabled: boolean;
  } | null;
  memberships: {
    tenantId: string;
    tenantName: string;
    role: string;
  }[];
}

export const findUser = async (query: string): Promise<UserLookupResult> =>
  (await padminTenantsC({ action: 'findUser', query })).data;
export const listSubscribers = async (tenantId: string, q?: string) =>
  (await padminTenantsC({ action: 'listSubscribers', tenantId, q })).data.subscribers;
export const upsertSubscriber = async (p: { tenantId: string; id?: string; phoneE164: string; name?: string; labels?: string[] }) =>
  (await padminTenantsC({ action: 'upsertSubscriber', ...p })).data;
export const deleteSubscriber = async (tenantId: string, id: string) =>
  (await padminTenantsC({ action: 'deleteSubscriber', tenantId, id })).data;
export const listAudit = async (tenantId?: string, limit?: number) =>
  (await padminAuditC({ action: 'listAudit', tenantId, limit })).data.entries;

// ── invites (Phase 1 gap #14) ────────────────────────────────────────────────
export interface AdminInvite {
  id: string; email: string; role: 'agent' | 'admin';
  status: 'pending' | 'accepted' | 'revoked'; createdAt: number;
}

export const inviteMember = async (p: { tenantId: string; email: string; role: 'agent' | 'admin'; reason?: string }) =>
  (await padminTenantsC({ action: 'inviteMember', ...p })).data;
export const listInvites = async (tenantId: string) =>
  (await padminTenantsC({ action: 'listInvites', tenantId })).data.invites;
export const revokeInvite = async (tenantId: string, inviteId: string) =>
  (await padminTenantsC({ action: 'revokeInvite', tenantId, inviteId })).data;
